import React from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Langs from "../../../../app/lang/langs";
import { setAddElementModalVisibility } from "../../vcompSlice";
import MobileDragTree from "./MobileDragTree";

const ORANGE = "#FF9015";

/**
 * MobileEditFormTab - Mobile version of EditFormTab.
 * Uses PrimeReact Tree with native touch drag-drop (same as desktop)
 * wrapped in mobile-friendly MUI containers.
 */
export default function MobileEditFormTab({
    isMobile,
    mode,
    nodesInProject,
    expandedKeysEdit,
    onToggleExpanded,
    expandAll,
    collapseAll,
    nodeTemplate,
    onNodeDoubleClick,
    onDragDrop,
    projectAptitudes,
    onCreateProject,
    onSetExcelModalVisibility,
    onExtractExcelProject,
    onPrevious,
    onNext,
}) {
    const dispatch = useDispatch();

    const position = projectAptitudes?.categories?.length || 0;

    return (
        <Box
            sx={{
                mt: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
            }}
        >
            {/* Header toolbar */}
            <Box
                sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 1.5,
                    backgroundColor: "var(--surface-card, #fff)",
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                    flexShrink: 0,
                }}
            >
                {/* Title row */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, fontSize: "1rem" }}
                    >
                        <Langs str="EDIT_PROJECT_FORM" />
                    </Typography>

                    {/* Add element — prominent on the right */}
                    <IconButton
                        onClick={() => {
                            dispatch(
                                setAddElementModalVisibility({
                                    visibility: true,
                                    element: null,
                                    subcategory: false,
                                    pos: position,
                                })
                            );
                        }}
                        sx={{
                            color: "#fff",
                            backgroundColor: ORANGE,
                            "&:hover": { backgroundColor: "#e67e00" },
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            boxShadow: "0 2px 6px rgba(255,144,21,0.35)",
                        }}
                    >
                        <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* Actions row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                        icon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Expand All" />}
                        variant="outlined"
                        onClick={expandAll}
                        sx={{
                            height: 32,
                            fontSize: "0.78rem",
                            fontWeight: 500,
                            borderRadius: "8px",
                            borderColor: "rgba(0,0,0,0.12)",
                            "& .MuiChip-icon": { ml: 0.5 },
                        }}
                    />
                    <Chip
                        icon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Collapse All" />}
                        variant="outlined"
                        onClick={collapseAll}
                        sx={{
                            height: 32,
                            fontSize: "0.78rem",
                            fontWeight: 500,
                            borderRadius: "8px",
                            borderColor: "rgba(0,0,0,0.12)",
                            "& .MuiChip-icon": { ml: 0.5 },
                        }}
                    />

                    <Box sx={{ flex: 1 }} />

                    {/* Excel import */}
                    <IconButton
                        onClick={() => onSetExcelModalVisibility(true)}
                        sx={{
                            color: "text.secondary",
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            border: "1px solid rgba(0,0,0,0.1)",
                            backgroundColor: "rgba(0,0,0,0.02)",
                        }}
                    >
                        <UploadFileIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Excel export (only in edit mode with project name) */}
                    {mode === "edit" && onCreateProject && onCreateProject.nom && (
                        <IconButton
                            onClick={() => onExtractExcelProject(nodesInProject, onCreateProject.nom)}
                            sx={{
                                color: "text.secondary",
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                border: "1px solid rgba(0,0,0,0.1)",
                                backgroundColor: "rgba(0,0,0,0.02)",
                            }}
                        >
                            <DownloadIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Touch-friendly tree with drag-and-drop */}
            <Box sx={{ flex: 1, overflow: "hidden" }}>
                <MobileDragTree
                    value={nodesInProject}
                    expandedKeys={expandedKeysEdit}
                    onToggle={onToggleExpanded}
                    nodeTemplate={nodeTemplate}
                    onNodeDoubleClick={onNodeDoubleClick}
                    onDragDrop={onDragDrop}
                    filter={true}
                    filterPlaceholder="Search..."
                />
            </Box>

            {/* Sticky bottom navigation */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    gap: 1.5,
                    px: 2.5,
                    py: 2,
                    pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                    background: "linear-gradient(to top, rgba(255,255,255,0.92) 50%, rgba(255,255,255,0.78) 100%)",
                    backdropFilter: "blur(18px) saturate(1.6)",
                    WebkitBackdropFilter: "blur(18px) saturate(1.6)",
                    borderTop: "1px solid rgba(255, 144, 21, 0.15)",
                    boxShadow: "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.04)",
                    zIndex: 10,
                }}
            >
                {mode !== "edit" && onPrevious && (
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onPrevious}
                        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "16px !important" }} />}
                        sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            minHeight: 50,
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            letterSpacing: "-0.01em",
                            border: "1.5px solid rgba(0, 0, 0, 0.12)",
                            color: "#4b5263",
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                borderColor: "rgba(255, 144, 21, 0.35)",
                                backgroundColor: "rgba(255, 144, 21, 0.06)",
                                color: "#FF9015",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="Previous" />
                    </Button>
                )}
                <Button
                    fullWidth
                    variant="contained"
                    onClick={onNext}
                    sx={{
                        borderRadius: "14px",
                        textTransform: "none",
                        minHeight: 50,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        letterSpacing: "-0.01em",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                        backgroundColor: "#FF9015",
                        color: "#fff",
                        border: "none",
                        boxShadow: "0 4px 16px rgba(255, 144, 21, 0.40), 0 8px 32px rgba(255, 144, 21, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                        transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #FF9015 0%, #FFB74D 100%)",
                            backgroundColor: "#FF9015",
                            boxShadow: "0 6px 24px rgba(255, 144, 21, 0.50), 0 12px 40px rgba(255, 144, 21, 0.25)",
                            transform: "translateY(-2px)",
                        },
                        "&:active": {
                            transform: "scale(0.98)",
                            boxShadow: "0 2px 10px rgba(255, 144, 21, 0.35)",
                        },
                    }}
                >
                    {mode === "edit" ? <Langs str="SAVE" /> : <Langs str="Next" />}
                </Button>
            </Box>
        </Box>
    );
}
